/**
 * Adtaxi Block
 *
 * Injection block that loads the Adtaxi (Tealium) container tag (utag.js)
 * asynchronously. The full utag.js URL varies per property, so it is configured
 * as a single value (typically bound to a custom column) and used directly as
 * the script src.
 *
 * Example: https://tags.tiqcdn.com/utag/adtaxi/daltonluxuryliving.com/prod/utag.js
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const SCRIPT_ID = 'adtaxi-utag-script';

function AdtaxiTrackingComponent(_props: BlockProps) {
  // Token key stays 'profile' for backwards compatibility with existing field
  // mappings, but now holds the FULL utag.js URL (labelled "Script URL").
  const scriptUrl = useBlockToken('profile', { defaultValue: '' });
  const trackingAllowed = useTrackingAllowed('advertising');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    if (!trackingAllowed || !scriptUrl) return;

    // Only load a well-formed https URL; ignore anything else.
    let src: string;
    try {
      const parsed = new URL(scriptUrl);
      if (parsed.protocol !== 'https:') return;
      src = parsed.href;
    } catch {
      return;
    }

    if (document.getElementById(SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = src;
    script.type = 'text/javascript';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      mountedRef.current = false;
    };
  }, [trackingAllowed, scriptUrl]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'AdtaxiTracking',
  displayName: 'Adtaxi',
  tokens: {
    profile: {
      label: 'Script URL',
      type: 'text',
      description:
        'Full Adtaxi/Tealium utag.js URL for this property, e.g. ' +
        'https://tags.tiqcdn.com/utag/adtaxi/daltonluxuryliving.com/prod/utag.js. ' +
        'The entire URL varies per property.',
      required: true,
    },
  },
};

export const adtaxiTrackingBlock: BlockDefinition = {
  id: 'adtaxi-tracking',
  name: 'Adtaxi',
  description:
    'Adtaxi (Tealium) container tag. Loads utag.js asynchronously from the configured URL.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: AdtaxiTrackingComponent,
  contentSchema,
  tags: ['adtaxi', 'tealium', 'utag', 'tiqcdn', 'analytics', 'tracking', 'tag', 'marketing'],
};
